import React, { useState } from "react";
import {
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  TextField,
  Autocomplete,
} from "@mui/material";
import {
  Add,
  InfoOutlined,
  ModeEditOutlineTwoTone,
  FileDownloadOutlined,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
} from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import AppTable from "components/Shared/AppTable";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import {
  useLazyListVehicleFuelQuery,
  useLazyExportVehicleFuelQuery,
} from "store/services/vehicleFuelApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { exportExcelFile } from "utils/exportExcelFile";

const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  vehicleId: null,
  fuelType: "",
};

const VehicleFuelListScreen = () => {
  const [sortOrder, setSortOrder] = useState("createdAt:-1");
  const [fuelType, setFuelType] = useState("");
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const [showDialog, setShowDialog] = useState(false);
  const [showVehicleFilter, setShowVehicleFilter] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Vehicle Fuel" }];

  const vehicleFuelTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.fuelId,
      cell: (row) => (
        <Link
          title={row?.fuelId}
          to={`/app/crm/vehicle-fuel/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.fuelId ? (
            <HighlightedText text={row?.fuelId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Vehicle",
      selector: (row) => row?.vehicle?.registration_number,
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.vehicle?.registration_number ? (
              <HighlightedText
                text={row.vehicle.registration_number}
                highlight={keyword}
              />
            ) : (
              "N/A"
            )}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.vehicle?.vehicle_id ? (
              <HighlightedText
                text={row.vehicle.vehicle_id}
                highlight={keyword}
              />
            ) : (
              "N/A"
            )}
          </span>
        </div>
      ),
    },
    {
      name: "Fuel Type",
      selector: (row) => row?.fuel?.[0]?.fuelType,
      cell: (row) =>
        row?.fuel?.[0]?.fuelType ? (
          <HighlightedText text={row.fuel[0].fuelType} highlight={keyword} />
        ) : (
          "N/A"
        ),
    },
    {
      name: "Merchant",
      selector: (row) => row?.merchant?.merchantName,
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.merchant?.merchantName ? (
              <HighlightedText
                text={row?.merchant?.merchantName}
                highlight={keyword}
              />
            ) : (
              "N/A"
            )}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.merchant?.merchantCode ? (
              <HighlightedText
                text={row?.merchant?.merchantCode}
                highlight={keyword}
              />
            ) : (
              "N/A"
            )}
          </span>
        </div>
      ),
    },
    {
      name: "Total Cost",
      selector: (row) =>
        row?.fuel?.reduce((sum, f) => sum + (f.totalCost || 0), 0).toFixed(2),
      cell: (row) =>
        row?.fuel?.length
          ? `Rs. ${row.fuel
              .reduce((sum, f) => sum + (f.totalCost || 0), 0)
              .toFixed(2)}`
          : "N/A",
    },
    {
      name: "Fuel Filled Date",
      selector: (row) => <AppTableDateTime date={row?.fuelDate} />,
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdByName,
      cell: (row) => (row?.createdByName ? row?.createdByName : " "),
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle-fuel/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Fuel Record"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/vehicle-fuel/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Fuel Record">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchVehicleFuel,
    { data: vehicleFuelRs, isLoading, isFetching, error },
  ] = useLazyListVehicleFuelQuery();
  const [
    exportVehicleFuel,
    { isLoading: isExporting, isFetching: isExportFetching },
  ] = useLazyExportVehicleFuelQuery();
  const [
    fetchVehicleDDL,
    { data: vehicleDDL, isLoading: isDDLLoaded, error: ddlError },
  ] = useLazyGetDDLVehicleListQuery();

  React.useEffect(() => {
    const [sortedBy, sortDirection] = sortOrder.split(":");
    const queryParams = {
      active:
        activeTab === "Active"
          ? true
          : activeTab === "Inactive"
          ? false
          : undefined,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...(fuelType && { fuelType }),
    };
    fetchVehicleFuel(queryParams);
  }, [
    activeTab,
    fetchVehicleFuel,
    currentPage,
    perPage,
    keyword,
    sortOrder,
    fuelType,
  ]);

  // Fetch vehicle DDL when the modal opens
  React.useEffect(() => {
    if (showDialog) {
      fetchVehicleDDL({ active: activeTab === "Active" ? true : false });
    }
  }, [showDialog, activeTab, fetchVehicleDDL]);

  const handleDownload = async (data) => {
    const { dateRange, vehicleId, fuelType } = data;
    if (!dateRange || dateRange.length !== 2) {
      toast.error("Please select a valid date range!");
      return;
    }

    const startDate = moment(dateRange[0]).toISOString();
    const endDate = moment(dateRange[1]).toISOString();

    const [sortedBy, sortDirection] = sortOrder.split(":");

    const payload = {
      active:
        activeTab === "Active"
          ? true
          : activeTab === "Inactive"
          ? false
          : undefined,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...(vehicleId && { vehicleId }),
      ...(fuelType && { fuelType }),
      startDate,
      endDate,
    };

    try {
      const response = await exportVehicleFuel(payload).unwrap();
      if (!response || response.length === 0) {
        toast.error("No Data Found!");
        return;
      }

      const formattedData = response.map((row, index) => ({
        SN: index + 1,
        "Fuel ID": row.fuelId || "N/A",
        "Vehicle Registration": row.vehicleRegistration || "N/A",
        "Vehicle ID": row.vehicleId || "N/A",
        "Fuel Type": row.fuelType || "N/A",
        "Fuel Quantity": row.quantity || "N/A",
        Merchant: row.merchant || "N/A",
        "Odometer (km)": row.odometer || "N/A",
        "Total Cost (Rs)": row.totalCost || "N/A",
        "Fuel Filled Date": row.fuelDate
          ? new Date(row.fuelDate).toLocaleDateString()
          : "N/A",
        "Created Date": row.createdAt
          ? new Date(row.createdAt).toLocaleDateString()
          : "N/A",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `VehicleFuel_${vehicleId?.vehicle_id || "All"}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data! Please try again.");
    } finally {
      handleReset();
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowVehicleFilter(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error
        error={error || (ddlError && "Failed to load vehicle dropdown data")}
      />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={vehicleFuelTabs}
          defaultValue={activeTab}
          actions={
            <Tooltip title="Download Excel" arrow>
              <IconButton color="success" onClick={() => setShowDialog(true)}>
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
          }
        />

        <TableFilter
          searchLabel="Fuel ID / Vehicle Reg.No / ID / Merchant"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Fuel Type"
            value={fuelType}
            onChange={(e) => setFuelType(e.target.value)}
            options={[
              { label: "All Types", value: "" },
              { label: "Petrol", value: "petrol" },
              { label: "Diesel", value: "diesel" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={vehicleFuelRs?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={vehicleFuelRs?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

        <AppConfirmDialog
          open={showDialog}
          onClose={handleReset}
          onConfirm={handleSubmit(handleDownload)}
          title="Download Vehicle Fuel"
          loading={isExporting || isExportFetching}
          dialogIcon={() => <FileDownloadOutlined color="success" />}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showVehicleFilter}
                onChange={(e) => setShowVehicleFilter(e.target.checked)}
                icon={<CheckBoxOutlineBlankOutlined />}
                checkedIcon={<CheckBoxOutlined />}
              />
            }
            label="Filter by specific Vehicle"
          />

          {showVehicleFilter && (
            <Controller
              name="vehicleId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  size="small"
                  options={[
                    { value: null, label: "All Vehicles" },
                    ...(vehicleDDL || []).map((item) => ({
                      value: item._id,
                      label: `${item.vehicle_id || "N/A"} - ${
                        item.registration_number || "No Reg No"
                      }`,
                    })),
                  ]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    (vehicleDDL || [])
                      .map((item) => ({
                        value: item._id,
                        label: `${item.vehicle_id || "N/A"} - ${
                          item.registration_number || "No Reg No"
                        }`,
                      }))
                      .find((opt) => opt.value === value) || {
                      value: null,
                      label: "All Vehicles",
                    }
                  }
                  onChange={(e, newValue) => onChange(newValue?.value || null)}
                  disableClearable
                  disabled={
                    isDDLLoaded || !vehicleDDL || vehicleDDL.length === 0
                  }
                  sx={{ mb: 2 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Vehicle" fullWidth />
                  )}
                />
              )}
            />
          )}

          <Controller
            name="fuelType"
            control={control}
            render={({ field }) => (
              <BaseSelect
                {...field}
                fullWidth
                label="Fuel Type"
                options={[
                  { label: "All Types", value: "" },
                  { label: "Petrol", value: "petrol" },
                  { label: "Diesel", value: "diesel" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                sx={{ mb: 2 }}
              />
            )}
          />

          <Controller
            name="dateRange"
            control={control}
            rules={{ required: "Date range is required" }}
            render={({ field, fieldState: { error } }) => (
              <DateRangeToggle
                {...field}
                label="Select Date Range"
                error={!!error}
                helperText={error?.message}
                required
              />
            )}
          />
        </AppConfirmDialog>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VehicleFuelListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/vehicle-fuel/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          New Vehicle Fuel
        </Button>
      </Link>
    </>
  );
};
